/**
 * Push Notification Manager
 * Handles service worker registration, push subscription, and permission management
 * Task 39: Web Push Notifications with SMS Failover
 */

import { API_BASE_URL } from '../config/api';

/**
 * Check if push notifications are supported in this browser
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Detect iOS
 */
export function isIos() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detect if running as installed PWA on iOS
 */
export function isIosPwa() {
  return isIos() && window.navigator.standalone === true;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[PUSH] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('[PUSH] Service worker registered', { scope: registration.scope });
    return registration;
  } catch (error) {
    console.error('[PUSH] Service worker registration failed', error);
    return null;
  }
}

/**
 * Request notification permission from the user
 */
export async function requestPushPermission() {
  if (!('Notification' in window)) {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  console.log('[PUSH] Permission result:', permission);
  return permission;
}

/**
 * Convert a base64 VAPID key to Uint8Array for PushManager
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications
 * 1. Gets VAPID key from backend
 * 2. Subscribes via PushManager
 * 3. Sends subscription to backend
 */
export async function subscribeToPush(apiCall) {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported in this browser');
  }

  // Ensure service worker is registered
  const registration = await navigator.serviceWorker.ready;

  // Get VAPID public key from backend
  const vapidResponse = await apiCall(`${API_BASE_URL}/push/vapid-public-key`);
  if (!vapidResponse.success || !vapidResponse.data?.publicKey) {
    throw new Error('Failed to get VAPID public key');
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidResponse.data.publicKey);

  // Subscribe via PushManager
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  const subscriptionJson = subscription.toJSON();

  // Send subscription to backend
  await apiCall(`${API_BASE_URL}/push/subscribe`, {
    method: 'POST',
    body: JSON.stringify({
      endpoint: subscriptionJson.endpoint,
      keys: {
        p256dh: subscriptionJson.keys.p256dh,
        auth: subscriptionJson.keys.auth,
      },
    }),
  });

  console.log('[PUSH] Successfully subscribed to push notifications');
  return subscription;
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(apiCall) {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    const endpoint = subscription.endpoint;

    await subscription.unsubscribe();

    // Tell backend to remove the subscription
    try {
      await apiCall(`${API_BASE_URL}/push/unsubscribe`, {
        method: 'POST',
        body: JSON.stringify({ endpoint }),
      });
    } catch (e) {
      console.warn('[PUSH] Failed to notify backend of unsubscription', e);
    }
  }

  console.log('[PUSH] Unsubscribed from push notifications');
}

/**
 * Get current subscription status
 */
export async function getSubscriptionStatus() {
  if (!isPushSupported()) {
    return { supported: false, subscribed: false, permission: 'default' };
  }

  const permission = Notification.permission;
  let subscribed = false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    subscribed = !!subscription;
  } catch (e) {
    // Service worker not ready yet
  }

  return { supported: true, subscribed, permission };
}
